<template>
  <Content class="message-center">
    <Card>
      <div slot="title">
        <span>消息中心</span>
        <div style="float: right;">
          <Button 
            type="primary" 
            size="small" 
            @click="markAllRead"
            :disabled="unreadCount === 0"
          >
            全部标为已读
          </Button>
          <Button 
            type="error" 
            size="small" 
            style="margin-left: 8px;"
            @click="deleteSelected"
            :disabled="selectedIds.length === 0"
          >
            批量删除
          </Button>
        </div>
      </div>

      <Spin v-if="loading" fix></Spin>

      <!-- 消息类型Tab -->
      <Tabs v-model="activeTab" @on-click="onTabChange">
        <TabPane label="全部消息" name="all">
          <Badge :count="totalUnread" class="tab-badge"></Badge>
        </TabPane>
        <TabPane label="系统通知" name="system">
          <Badge :count="systemUnread" class="tab-badge"></Badge>
        </TabPane>
        <TabPane label="业务通知" name="business">
          <Badge :count="businessUnread" class="tab-badge"></Badge>
        </TabPane>
        <TabPane label="告警信息" name="alert">
          <Badge :count="alertUnread" class="tab-badge"></Badge>
        </TabPane>
      </Tabs>

      <!-- 消息列表 -->
      <div class="message-list">
        <CheckboxGroup v-model="selectedIds">
          <div 
            v-for="msg in messages" 
            :key="msg._id"
            class="message-item"
            :class="{ unread: !msg.isRead }"
          >
            <div class="message-checkbox">
              <Checkbox :label="msg._id"></Checkbox>
            </div>
            <div class="message-icon">
              <Icon :type="getMessageIcon(msg.type)" :color="getMessageColor(msg.type)" size="24" />
            </div>
            <div class="message-content" @click="viewMessage(msg)">
              <div class="message-title">
                <span class="title-text">{{ msg.title }}</span>
                <Tag v-if="!msg.isRead" color="primary" size="small">未读</Tag>
                <Tag :color="getTypeColor(msg.type)" size="small">{{ getTypeName(msg.type) }}</Tag>
              </div>
              <div class="message-body">{{ msg.content }}</div>
              <div class="message-time">{{ formatTime(msg.createTime) }}</div>
            </div>
            <div class="message-actions">
              <Button 
                v-if="!msg.isRead" 
                type="text" 
                size="small" 
                @click.stop="markRead([msg._id])"
              >
                标为已读
              </Button>
              <Button 
                type="text" 
                size="small" 
                @click.stop="deleteMessage([msg._id])"
              >
                删除
              </Button>
            </div>
          </div>
        </CheckboxGroup>

        <!-- 空状态 -->
        <div v-if="messages.length === 0 && !loading" class="empty-state">
          <Icon type="ios-mail-outline" size="64" color="#c0c4cc" />
          <p>暂无消息</p>
        </div>
      </div>

      <!-- 分页 -->
      <Page 
        v-show="total > 0"
        :total="total"
        :current="pageNo"
        :page-size="pageSize"
        :page-size-opts="[10, 20, 50]"
        show-sizer
        show-elevator
        show-total
        @on-change="pageChange"
        @on-page-size-change="pageSizeChange"
        style="margin-top: 16px; text-align: right;"
      />
    </Card>

    <!-- 消息详情弹窗 -->
    <Modal 
      v-model="detailModal" 
      :title="currentMessage ? currentMessage.title : '消息详情'"
      width="600"
    >
      <div v-if="currentMessage" class="message-detail">
        <div class="detail-header">
          <Tag :color="getTypeColor(currentMessage.type)">{{ getTypeName(currentMessage.type) }}</Tag>
          <span class="detail-time">{{ formatTime(currentMessage.createTime) }}</span>
        </div>
        <Divider />
        <div class="detail-content" v-html="currentMessage.content"></div>
        
        <!-- 如果是告警类型，显示处理选项 -->
        <div v-if="currentMessage.type === 'alert' && !currentMessage.handled" class="alert-actions">
          <Divider />
          <Form :model="alertForm" :label-width="80">
            <FormItem label="处理方式">
              <Select v-model="alertForm.action" style="width: 200px;">
                <Option value="resolved">已处理</Option>
                <Option value="ignored">忽略</Option>
                <Option value="escalated">升级处理</Option>
              </Select>
            </FormItem>
            <FormItem label="处理备注">
              <Input v-model="alertForm.remark" type="textarea" :rows="2" placeholder="请输入处理备注" />
            </FormItem>
            <FormItem>
              <Button type="primary" @click="handleAlertAction">提交处理</Button>
            </FormItem>
          </Form>
        </div>
        
        <div v-if="currentMessage.handled" class="handled-info">
          <Divider />
          <Alert type="success">
            <p><strong>处理状态：</strong>{{ currentMessage.handleAction }}</p>
            <p><strong>处理时间：</strong>{{ formatTime(currentMessage.handleTime) }}</p>
            <p v-if="currentMessage.handleRemark"><strong>处理备注：</strong>{{ currentMessage.handleRemark }}</p>
          </Alert>
        </div>
      </div>
    </Modal>
  </Content>
</template>

<script>
import { 
  getNotificationList, 
  getUnreadCount, 
  markAsRead, 
  markAllAsRead, 
  deleteNotification,
  handleAlert
} from '@/api/notification'

export default {
  name: 'MessageCenter',
  data() {
    return {
      loading: false,
      activeTab: 'all',
      messages: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      selectedIds: [],
      totalUnread: 0,
      systemUnread: 0,
      businessUnread: 0,
      alertUnread: 0,
      detailModal: false,
      currentMessage: null,
      alertForm: {
        action: 'resolved',
        remark: ''
      }
    }
  },
  computed: {
    unreadCount() {
      return this.totalUnread
    }
  },
  methods: {
    async loadMessages() {
      this.loading = true
      try {
        const params = {
          page: this.pageNo,
          pageSize: this.pageSize,
          type: this.activeTab === 'all' ? null : this.activeTab
        }
        
        const res = await getNotificationList(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.messages = res.data.data?.list || []
          this.total = res.data.data?.pagination?.total || 0
        } else {
          this.$Message.error(res.data?.message || '获取消息列表失败')
        }
      } catch (error) {
        this.$Message.error('获取消息列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async loadUnreadCount() {
      try {
        const res = await getUnreadCount()
        if (res.data && (res.data.success || res.data.code === 200)) {
          const data = res.data.data || {}
          this.totalUnread = data.total || 0
          this.systemUnread = data.system || 0
          this.businessUnread = data.business || 0
          this.alertUnread = data.alert || 0
        }
      } catch (error) {
        console.error('获取未读数量失败:', error)
      }
    },
    async markRead(ids) {
      try {
        const res = await markAsRead(ids)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('已标为已读')
          this.loadMessages()
          this.loadUnreadCount()
        }
      } catch (error) {
        this.$Message.error('操作失败')
      }
    },
    async markAllRead() {
      try {
        const res = await markAllAsRead()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('全部标为已读')
          this.loadMessages()
          this.loadUnreadCount()
        }
      } catch (error) {
        this.$Message.error('操作失败')
      }
    },
    async deleteMessage(ids) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除选中的 ${ids.length} 条消息吗？`,
        onOk: async () => {
          try {
            const res = await deleteNotification(ids)
            if (res.data && (res.data.success || res.data.code === 200)) {
              this.$Message.success('删除成功')
              this.selectedIds = []
              this.loadMessages()
              this.loadUnreadCount()
            }
          } catch (error) {
            this.$Message.error('删除失败')
          }
        }
      })
    },
    deleteSelected() {
      if (this.selectedIds.length === 0) {
        this.$Message.warning('请先选择要删除的消息')
        return
      }
      this.deleteMessage(this.selectedIds)
    },
    viewMessage(msg) {
      this.currentMessage = msg
      this.detailModal = true
      
      // 如果消息未读，标记为已读
      if (!msg.isRead) {
        this.markRead([msg._id])
      }
    },
    async handleAlertAction() {
      if (!this.currentMessage) return
      
      try {
        const res = await handleAlert(
          this.currentMessage._id,
          this.alertForm.action,
          this.alertForm.remark
        )
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('处理成功')
          this.detailModal = false
          this.loadMessages()
          this.alertForm = { action: 'resolved', remark: '' }
        } else {
          this.$Message.error(res.data?.message || '处理失败')
        }
      } catch (error) {
        this.$Message.error('处理失败')
      }
    },
    onTabChange() {
      this.pageNo = 1
      this.selectedIds = []
      this.loadMessages()
    },
    pageChange(page) {
      this.pageNo = page
      this.loadMessages()
    },
    pageSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.loadMessages()
    },
    getMessageIcon(type) {
      const icons = {
        system: 'ios-information-circle',
        business: 'ios-document',
        alert: 'ios-warning',
        announcement: 'ios-megaphone'
      }
      return icons[type] || 'ios-mail'
    },
    getMessageColor(type) {
      const colors = {
        system: '#409EFF',
        business: '#67C23A',
        alert: '#F56C6C',
        announcement: '#E6A23C'
      }
      return colors[type] || '#909399'
    },
    getTypeName(type) {
      const names = {
        system: '系统通知',
        business: '业务通知',
        alert: '告警信息',
        announcement: '公告'
      }
      return names[type] || '其他'
    },
    getTypeColor(type) {
      const colors = {
        system: 'primary',
        business: 'success',
        alert: 'error',
        announcement: 'warning'
      }
      return colors[type] || 'default'
    },
    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      // 1分钟内
      if (diff < 60 * 1000) {
        return '刚刚'
      }
      // 1小时内
      if (diff < 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 1000))}分钟前`
      }
      // 24小时内
      if (diff < 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 60 * 1000))}小时前`
      }
      // 7天内
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (24 * 60 * 60 * 1000))}天前`
      }
      
      return date.toLocaleString('zh-CN')
    }
  },
  mounted() {
    this.loadMessages()
    this.loadUnreadCount()
  }
}
</script>

<style lang="less" scoped>
.message-center {
  .tab-badge {
    position: absolute;
    top: -8px;
    right: -8px;
  }
  
  .message-list {
    margin-top: 16px;
    
    .message-item {
      display: flex;
      align-items: flex-start;
      padding: 16px;
      border-bottom: 1px solid #f0f0f0;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f9f9f9;
      }
      
      &.unread {
        background-color: #f5f7fa;
        
        .message-title .title-text {
          font-weight: bold;
        }
      }
      
      .message-checkbox {
        margin-right: 12px;
        padding-top: 4px;
      }
      
      .message-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
      }
      
      .message-content {
        flex: 1;
        cursor: pointer;
        
        .message-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          
          .title-text {
            font-size: 14px;
            color: #303133;
          }
        }
        
        .message-body {
          font-size: 13px;
          color: #606266;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .message-time {
          font-size: 12px;
          color: #909399;
          margin-top: 8px;
        }
      }
      
      .message-actions {
        display: flex;
        gap: 4px;
        flex-shrink: 0;
      }
    }
    
    .empty-state {
      text-align: center;
      padding: 60px 0;
      color: #c0c4cc;
      
      p {
        margin-top: 16px;
        font-size: 14px;
      }
    }
  }
  
  .message-detail {
    .detail-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .detail-time {
        font-size: 12px;
        color: #909399;
      }
    }
    
    .detail-content {
      font-size: 14px;
      line-height: 1.8;
      color: #606266;
    }
    
    .alert-actions {
      margin-top: 16px;
    }
  }
}
</style>

